import { fetchGuestToken } from '../api';
import { StorageKey } from '../enums';
import { WebStoreApiTokensRequestBody } from '../models';
import { isTokenExpired } from './is-token-expired';
import { refreshToken } from './refresh-token';
import { validateJWTToken } from './validate-jwt-token';

import { getWebStoreApiConfig } from '../config';

/**
 * Gets guest JWT from either storage or Web Store API.
 * If JWT in storage is about to be expired it will be refreshed.
 * @returns Promise with guest JWT
 */
export const getGuestToken = async (): Promise<string | null> => {
  const { storage } = getWebStoreApiConfig();
  const storedToken: string | null = storage.getItem(StorageKey.WebStoreGuestToken);
  if (storedToken && validateJWTToken(storedToken)) {
    return storedToken;
  }

  let token: string | null;
  let customerId: string | null = null;

  if (storedToken && !isTokenExpired(storedToken)) {
    // If token is not expired then it will be refreshed.
    token = await refreshToken(storedToken);
  } else {
    // If token either does not exist or is expired it will be replaced with new one.
    const { marketLocale, siteContext } = getWebStoreApiConfig();
    if (!siteContext) {
      console.error('getGuestToken called before siteContext was configured');
      return null;
    }
    const body: WebStoreApiTokensRequestBody = { brand: siteContext, marketLocale };
    const response = await fetchGuestToken(body);

    token = response.token;
    customerId = response.customerId;
  }

  if (!token) {
    return Promise.reject('Authorization header was not found in guest token response');
  }

  storage?.setItem(StorageKey.WebStoreGuestToken, token);
  if (customerId) {
    storage?.setItem(StorageKey.CustomerGuestId, customerId);
  }

  return token;
};
